import { POWER_UPS, type PowerUpId } from '@accra/shared';
import { BoxGeometry, CylinderGeometry, Group, InstancedMesh, Matrix4, Mesh, MeshStandardMaterial, Object3D } from 'three';
import type { Pickup } from '../../game/RunState';
import { canvasTexture } from '../../render/materials/canvasTexture';

const MAX_COINS = 160;
const MAX_CRATES = 6;
const CRATE_COLORS: Record<PowerUpId, string> = { shield: '#2a9d8f', magnet: '#e63946', double: '#fcd116', siren: '#1d4ed8', launch: '#f77f00' };
const HIDDEN = new Matrix4().makeScale(0, 0, 0);

function crateMaterial(id: PowerUpId): MeshStandardMaterial {
  const icon = POWER_UPS.find((p) => p.id === id)?.icon ?? '?';
  return new MeshStandardMaterial({
    map: canvasTexture(128, 128, (ctx) => {
      ctx.fillStyle = CRATE_COLORS[id];
      ctx.fillRect(0, 0, 128, 128);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 10;
      ctx.strokeRect(6, 6, 116, 116);
      ctx.font = '72px serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(icon, 64, 70);
    }),
  });
}

/** Pesewa coins (instanced, spinning) and power-up crates (bobbing). */
export class PickupsView {
  readonly group = new Group();
  private readonly coins: InstancedMesh;
  private readonly crates: Mesh[] = [];
  private readonly materials = new Map<PowerUpId, MeshStandardMaterial>();
  private readonly helper = new Object3D();
  private clock = 0;

  constructor() {
    const coin = new CylinderGeometry(0.35, 0.35, 0.08, 16).rotateX(Math.PI / 2);
    this.coins = new InstancedMesh(coin, new MeshStandardMaterial({ color: '#e0b43c', metalness: 0.7, roughness: 0.3, emissive: '#3a2a00' }), MAX_COINS);
    this.coins.frustumCulled = false;
    this.group.add(this.coins);
    const box = new BoxGeometry(1.1, 1.1, 1.1);
    for (let i = 0; i < MAX_CRATES; i += 1) {
      const crate = new Mesh(box, crateMaterial('shield'));
      crate.castShadow = true;
      crate.visible = false;
      this.crates.push(crate);
      this.group.add(crate);
    }
    for (const p of POWER_UPS) this.materials.set(p.id, crateMaterial(p.id));
  }

  sync(pickups: readonly Pickup[], dt: number): void {
    this.clock += dt;
    let coinIndex = 0;
    let crateIndex = 0;
    for (const pickup of pickups) {
      if (pickup.kind === 'coin' && coinIndex < MAX_COINS) {
        this.helper.position.set(pickup.x, pickup.y, -pickup.s);
        this.helper.rotation.set(0, this.clock * 4 + pickup.s, 0);
        this.helper.updateMatrix();
        this.coins.setMatrixAt(coinIndex++, this.helper.matrix);
      } else if (pickup.kind === 'crate' && pickup.power && crateIndex < MAX_CRATES) {
        const crate = this.crates[crateIndex++];
        if (!crate) continue;
        crate.visible = true;
        crate.material = this.materials.get(pickup.power) ?? crate.material;
        crate.position.set(pickup.x, pickup.y + Math.sin(this.clock * 3 + pickup.s) * 0.2, -pickup.s);
        crate.rotation.y = this.clock * 1.5;
      }
    }
    for (let i = coinIndex; i < MAX_COINS; i += 1) this.coins.setMatrixAt(i, HIDDEN);
    this.coins.instanceMatrix.needsUpdate = true;
    for (let i = crateIndex; i < MAX_CRATES; i += 1) {
      const crate = this.crates[i];
      if (crate) crate.visible = false;
    }
  }
}
